import { useMemo, useRef, useLayoutEffect, useState, useEffect } from 'react';
import { IOrgChart } from '@/states/organization.state';
import { ORG_DIVISIONS, ORG_LEVELS, getLevelIndex } from '@/utils/constants/organization-matrix';
import { normalizeDivisionKey, normalizeLevelKey, buildIOrgChartTree } from '@/utils/functions/organization.utils';

// ─── Constants ────────────────────────────────────────────────────────────────
const CARD_W = 180;
const CARD_H = 88; // approximate card height
const H_GAP = 40; // horizontal gap between sibling cards
const V_GAP = 60; // vertical gap between levels

// ─── Division badge colours ───────────────────────────────────────────────────
const DIVISION_COLORS_LIGHT: Record<string, { bg: string; text: string }> = {
  hr: { bg: '#dbeafe', text: '#1d4ed8' },
  marketing: { bg: '#ede9fe', text: '#7c3aed' },
  operation: { bg: '#e0f2fe', text: '#0369a1' },
  finance: { bg: '#dcfce7', text: '#15803d' },
};

const DIVISION_COLORS_DARK: Record<string, { bg: string; text: string }> = {
  hr: { bg: '#1e3a5f', text: '#93c5fd' },
  marketing: { bg: '#2e1a4f', text: '#c4b5fd' },
  operation: { bg: '#0c2d48', text: '#7dd3fc' },
  finance: { bg: '#14352a', text: '#6ee7b7' },
};

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function getDivisionLabel(key?: string | null): string {
  const found = ORG_DIVISIONS.find(d => d.key === normalizeDivisionKey(key));
  return found?.label ?? key ?? '';
}

function getLevelLabel(key?: string | null): string {
  const found = ORG_LEVELS.find(l => l.key === normalizeLevelKey(key));
  return found?.label ?? key ?? '';
}

// ─── Layout computation ───────────────────────────────────────────────────────
interface LayoutNode {
  node: IOrgChart;
  x: number; // center x
  y: number; // top y
  children: LayoutNode[];
  subtreeWidth: number;
}

/**
 * Resolves the hierarchy row a node belongs to, based on its `level_key`
 * (Direktur=0 … Staff=5) rather than its depth in the tree.
 *
 * Every node of the same level lands on the same row no matter how many
 * ancestors it has — a Staff reporting straight to a Direktur sits on the
 * Staff row, not on row 1, so the chart reads as one consistent hierarchy.
 *
 * `parentRow` is -1 for roots. Nodes whose level is missing or unrecognised
 * (root containers like "PT Demo", or legacy data without metadata) fall back
 * to sitting directly beneath their parent. The result is clamped below the
 * parent so malformed data can never place a child above its own parent.
 */
function resolveRowIndex(node: IOrgChart, parentRow: number): number {
  const levelRow = getLevelIndex(normalizeLevelKey(node.level_key));
  if (levelRow < 0) return parentRow + 1;
  return Math.max(levelRow, parentRow + 1);
}

function computeLayout(node: IOrgChart, parentRow = -1): LayoutNode {
  const row = resolveRowIndex(node, parentRow);
  const children = (node.children ?? []).map(c => computeLayout(c, row));

  let subtreeWidth: number;
  if (children.length === 0) {
    subtreeWidth = CARD_W;
  } else {
    const totalChildrenWidth = children.reduce((sum, c) => sum + c.subtreeWidth, 0);
    subtreeWidth = Math.max(CARD_W, totalChildrenWidth + H_GAP * (children.length - 1));
  }

  // x will be set in a second pass
  return { node, x: 0, y: row * (CARD_H + V_GAP), children, subtreeWidth };
}

function positionNode(layout: LayoutNode, startX: number): void {
  layout.x = startX + layout.subtreeWidth / 2;

  let childStartX =
    startX +
    (layout.subtreeWidth -
      (layout.children.reduce((s, c) => s + c.subtreeWidth, 0) + H_GAP * (layout.children.length - 1))) /
      2;
  for (const child of layout.children) {
    positionNode(child, childStartX);
    childStartX += child.subtreeWidth + H_GAP;
  }
}

function buildLayoutTree(root: IOrgChart): LayoutNode {
  const layout = computeLayout(root);
  positionNode(layout, 0);
  return layout;
}

// ─── Collect all nodes and edges from layout tree ────────────────────────────
function collectAll(
  layout: LayoutNode,
  nodes: LayoutNode[],
  edges: { x1: number; y1: number; x2: number; y2: number }[],
) {
  nodes.push(layout);
  for (const child of layout.children) {
    edges.push({
      x1: layout.x,
      y1: layout.y + CARD_H,
      x2: child.x,
      y2: child.y,
    });
    collectAll(child, nodes, edges);
  }
}

// ─── SVG connector paths ─────────────────────────────────────────────────────
function edgePath(x1: number, y1: number, x2: number, y2: number): string {
  const midY = (y1 + y2) / 2;
  if (Math.abs(x1 - x2) < 1) {
    return `M ${x1} ${y1} L ${x2} ${y2}`;
  }
  return `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
}

// ─── Node Card (absolutely positioned) ───────────────────────────────────────
interface NodeCardProps {
  layout: LayoutNode;
  isDark: boolean;
}

function NodeCard({ layout, isDark }: NodeCardProps) {
  const { node, x, y } = layout;
  const dk = normalizeDivisionKey(node.division_key) ?? '';
  const palette = isDark ? DIVISION_COLORS_DARK : DIVISION_COLORS_LIGHT;
  const colors = palette[dk] ?? (isDark ? { bg: '#374151', text: '#9ca3af' } : { bg: '#f3f4f6', text: '#6b7280' });
  const divLabel = getDivisionLabel(node.division_key);
  const levelLabel = getLevelLabel(node.level_key);

  return (
    <div
      style={{
        position: 'absolute',
        left: x - CARD_W / 2,
        top: y,
        width: CARD_W,
      }}
    >
      <div
        style={{
          background: isDark ? '#1e293b' : '#fff',
          border: `2px solid ${isDark ? '#334155' : '#bfdbfe'}`,
          borderRadius: 16,
          padding: '12px 14px',
          textAlign: 'center',
          boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(59,130,246,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          minHeight: CARD_H,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#93c5fd' : '#1d4ed8', lineHeight: 1.3 }}>
          {node.name}
        </span>
        {levelLabel && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: isDark ? '#94a3b8' : '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {levelLabel}
          </span>
        )}
        {divLabel && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '2px 8px',
              borderRadius: 6,
              background: colors.bg,
              color: colors.text,
            }}
          >
            {divLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Single tree renderer ─────────────────────────────────────────────────────
function OrgTree({ root, isDark }: { root: IOrgChart; isDark: boolean }) {
  const layout = useMemo(() => buildLayoutTree(root), [root]);

  const allNodes: LayoutNode[] = [];
  const allEdges: { x1: number; y1: number; x2: number; y2: number }[] = [];
  collectAll(layout, allNodes, allEdges);

  const maxX = Math.max(...allNodes.map(n => n.x + CARD_W / 2));
  const maxY = Math.max(...allNodes.map(n => n.y + CARD_H));
  const svgW = maxX + 20;
  const svgH = maxY + 20;
  const connectorColor = isDark ? '#334155' : '#bfdbfe';

  return (
    <div style={{ position: 'relative', width: svgW, height: svgH }}>
      {/* SVG connector layer */}
      <svg
        width={svgW}
        height={svgH}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        {allEdges.map((e, i) => (
          <path key={i} d={edgePath(e.x1, e.y1, e.x2, e.y2)} stroke={connectorColor} strokeWidth={2} fill="none" />
        ))}
      </svg>

      {/* Node cards */}
      {allNodes.map(n => (
        <NodeCard key={n.node.id} layout={n} isDark={isDark} />
      ))}
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────
interface OrgChartTreeViewProps {
  isLoading: boolean;
  nodes: IOrgChart[];
}

export default function OrgChartTreeView({ isLoading, nodes }: Readonly<OrgChartTreeViewProps>) {
  const roots = useMemo(() => buildIOrgChartTree(nodes), [nodes]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const isDark = useDarkMode();

  useLayoutEffect(() => {
    if (containerRef.current) {
      setScrollable(containerRef.current.scrollWidth > containerRef.current.clientWidth);
    }
  }, [roots]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-gray-400 dark:text-gray-500 text-sm">Memuat struktur organisasi...</p>
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-gray-400 dark:text-gray-500 text-sm">Belum ada struktur organisasi.</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'visible',
        background: isDark ? '#0f172a' : '#f8faff',
        borderRadius: 16,
        padding: '32px 24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 48,
          minWidth: 'max-content',
        }}
      >
        {roots.map(root => (
          <OrgTree key={root.id} root={root} isDark={isDark} />
        ))}
      </div>
      {scrollable && (
        <p style={{ textAlign: 'center', color: '#93c5fd', fontSize: 11, marginTop: 8 }}>
          ← Geser untuk melihat selengkapnya →
        </p>
      )}
    </div>
  );
}
